import { useDispatch, useSelector, useStore } from "react-redux";
import { useEffect } from "react";
import { RootState } from "../store";
import TaxPayer from "../models/tax-payer";
import { getItem, getList } from "../api/generics";
import { SUCCESS } from "../utils/constants/tags";
import Establishment from "../models/establishment";
import DispatchPoint from "../models/dispatch-point";
import Stamp from "../models/stamp";
import {
  setTaxPayerParameters,
  setTaxPayerParametersLoading,
  setTaxPayerParametersError,
  setTaxPayerParametersErrorDetails,
} from "../redux/tax-payer-parameters/tax-payer-parameters-slice";

interface ParametersProviderProps {
  children: React.ReactNode;
}

const ParametersProvider: React.FunctionComponent<ParametersProviderProps> = (
  props,
) => {
  const taxPayer = useSelector(
    (state: RootState) => state.taxPayer.data.taxPayer,
  );

  const dispatch = useDispatch();
  const store = useStore<RootState>();

  const loadTaxPayerParameters = async (
    taxPayer: TaxPayer,
    isCurrent: () => boolean,
  ) => {
    // A suspended company refuses these requests; the app shows the
    // suspension screen instead.
    if (taxPayer.id === undefined || taxPayer.accessBlocked) {
      return;
    }

    dispatch(setTaxPayerParametersLoading(true));
    dispatch(setTaxPayerParametersError(false));
    dispatch(setTaxPayerParametersErrorDetails([]));

    try {
      const additionalParams = new Map<string, string>();
      additionalParams.set("tax_payer_id", taxPayer.id.toString());

      // Load establishments
      const establishmentsPromise = getList<Establishment>(
        "/establishments/",
        100,
        0,
        additionalParams,
      );

      // Load dispatch points
      const dispatchPointsPromise = getList<DispatchPoint>(
        "/dispatch_points/",
        100,
        0,
        additionalParams,
      );

      // Load stamps
      const stampsAdditionalParams = new Map<string, string>();
      stampsAdditionalParams.set("tax_payer_id", taxPayer.id.toString());
      stampsAdditionalParams.set("enabled", "true");
      const stampsPromise = getList<Stamp>(
        "/stamps/",
        100,
        0,
        stampsAdditionalParams,
      );

      // Load taxpayer features (fixed limits visible to all users)
      const featuresPromise = getItem<{
        connectivityAccess: boolean | null;
        accountingAccess: boolean | null;
        aiAccess: boolean | null;
        aiEnabled: boolean | null;
        aiSettingsManageable: boolean;
        isDemo: boolean | null;
        whatsappEnabled: boolean;
      }>(`/tax_payers/${taxPayer.id}/features/`);

      const [establishmentsRes, dispatchPointsRes, stampsRes, featuresRes] =
        await Promise.all([
          establishmentsPromise,
          dispatchPointsPromise,
          stampsPromise,
          featuresPromise,
        ]);

      if (!isCurrent()) return;

      let establishments: Establishment[] = [];
      let dispatchPoints: DispatchPoint[] = [];
      let stamps: Stamp[] = [];

      if (
        establishmentsRes.status === SUCCESS &&
        establishmentsRes.data !== undefined
      ) {
        establishments = establishmentsRes.data.items;
      } else {
        dispatch(setTaxPayerParametersError(true));
        if (establishmentsRes.detail) {
          dispatch(
            setTaxPayerParametersErrorDetails([establishmentsRes.detail]),
          );
        }
      }

      if (
        dispatchPointsRes.status === SUCCESS &&
        dispatchPointsRes.data !== undefined
      ) {
        dispatchPoints = dispatchPointsRes.data.items;
      } else {
        dispatch(setTaxPayerParametersError(true));
        if (dispatchPointsRes.detail) {
          dispatch(
            setTaxPayerParametersErrorDetails([dispatchPointsRes.detail]),
          );
        }
      }

      if (stampsRes.status === SUCCESS && stampsRes.data !== undefined) {
        stamps = stampsRes.data.items;
      } else {
        dispatch(setTaxPayerParametersError(true));
        if (stampsRes.detail) {
          dispatch(setTaxPayerParametersErrorDetails([stampsRes.detail]));
        }
      }

      let connectivityAccess: boolean | null = false;
      let accountingAccess: boolean | null = null;
      let aiAccess: boolean | null = null;
      let aiEnabled: boolean | null = null;
      let aiSettingsManageable = false;
      let isDemo: boolean | null = null;
      let whatsappEnabled = false;
      if (featuresRes.status === SUCCESS && featuresRes.data !== undefined) {
        connectivityAccess = featuresRes.data.connectivityAccess;
        accountingAccess = featuresRes.data.accountingAccess;
        aiAccess = featuresRes.data.aiAccess;
        aiEnabled = featuresRes.data.aiEnabled;
        aiSettingsManageable = featuresRes.data.aiSettingsManageable;
        isDemo = featuresRes.data.isDemo;
        whatsappEnabled = featuresRes.data.whatsappEnabled === true;
      }

      dispatch(
        setTaxPayerParameters({
          establishments,
          dispatchPoints,
          stamps,
          connectivityAccess,
          accountingAccess,
          aiAccess,
          aiEnabled,
          aiSettingsManageable,
          isDemo,
          whatsappEnabled,
        }),
      );
    } catch (error) {
      if (!isCurrent()) return;
      dispatch(setTaxPayerParametersError(true));
      dispatch(
        setTaxPayerParametersErrorDetails(["Failed to load parameters"]),
      );
      dispatch(
        setTaxPayerParameters({
          establishments: [],
          dispatchPoints: [],
          stamps: [],
          connectivityAccess: false,
          accountingAccess: null,
          aiAccess: null,
          aiEnabled: null,
          aiSettingsManageable: false,
          isDemo: null,
        }),
      );
    } finally {
      if (isCurrent()) dispatch(setTaxPayerParametersLoading(false));
    }
  };

  useEffect(
    () => {
      let current = true;
      loadTaxPayerParameters(
        taxPayer,
        () =>
          current && store.getState().taxPayer.data.taxPayer.id === taxPayer.id,
      );
      return () => {
        current = false;
      };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [taxPayer],
  );

  return <>{props.children}</>;
};

export default ParametersProvider;
